import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin, Sun, PlugZap, ClipboardCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHeading } from "@/components/bits";
import { COMPANY, PRODUCTS, SERVICES, OFFICES, IMAGES, SALES_PHONE, WHATSAPP_LINK } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const MARQUEE_ITEMS = [
  "Solar PV Modules",
  "Solar Inverters",
  "Lithium Batteries & Energy Storage",
  "Mounting Structures",
  "DC & AC Cables",
  "MC4 Connectors",
  "BOS Components",
  "Cable Lugs & Terminals",
  "Electrical Components",
];

const CATEGORIES = [
  {
    icon: Sun,
    title: "Solar Energy Products",
    text: "PV modules, inverters, batteries, mounting structures, and balance-of-system components.",
    to: "/products",
  },
  {
    icon: PlugZap,
    title: "Electrical Components",
    text: "Wires and cables, connectors, lugs, terminals, and related electrical equipment.",
    to: "/products",
  },
  {
    icon: ClipboardCheck,
    title: "Project & Supply Support",
    text: "Site survey, system planning, and installation support arranged per the agreed scope.",
    to: "/services",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 64]);

  return (
    <>
      <Seo
        title="Solar Energy Products & Electrical Components | Ambrotech"
        description="Ambrotech supplies solar panels, inverters, batteries, cables, and electrical components for residential, commercial, and industrial requirements."
        path="/"
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-white" data-testid="home-hero">
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-brand opacity-[0.07] blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-12 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:pb-24 lg:pt-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
              Ambrotech Private Limited
            </motion.p>

            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {["Powering Industry,", "Building Trust."].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, delay: 0.12 + i * 0.13, ease: EASE }}
                  >
                    {i === 1 ? (
                      <>
                        Building <span className="text-brand">Trust.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {COMPANY.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.54, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/products"
                data-testid="hero-explore-products-button"
                className="group inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-navy-light"
              >
                Explore Products
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-us-button"
                className="inline-flex items-center rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                Contact Us
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.68 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500"
            >
              <a href={SALES_PHONE.tel} className="inline-flex items-center gap-2 font-medium transition-colors hover:text-navy" data-testid="hero-sales-phone-link">
                <Phone className="h-4 w-4 text-brand" /> {SALES_PHONE.label}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 font-medium transition-colors hover:text-navy" data-testid="hero-email-link">
                <Mail className="h-4 w-4 text-brand" /> {COMPANY.email}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="relative"
          >
            <div aria-hidden="true" className="absolute -right-3 -top-3 hidden h-full w-full rounded-2xl border border-brand/50 sm:block lg:-right-4 lg:-top-4" />
            <div className="relative overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(11,25,44,0.18)]">
              <motion.img
                src={IMAGES.hero}
                alt="Commercial rooftop solar panel installation"
                className="h-[320px] w-full object-cover sm:h-[420px] lg:h-[520px]"
                style={{ y: yImg }}
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-5 py-3.5 shadow-[0_12px_32px_rgba(11,25,44,0.14)]"
              data-testid="hero-supply-card"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand">
                <Sun className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold leading-tight text-navy">
                Solar Energy
                <span className="block text-xs font-medium text-slate-500">Electrical · Industrial Supply</span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section aria-label="Product categories" className="overflow-hidden border-y border-navy/10 bg-navy py-4">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} aria-hidden={dup === 1} className="flex shrink-0 items-center">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${dup}-${item}`} className="flex items-center gap-8 pr-8 font-display text-sm font-semibold uppercase tracking-[0.18em] text-white/80">
                  {item}
                  <span className="inline-block h-1.5 w-1.5 rotate-45 bg-brand" aria-hidden="true" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
        <SectionHeading
          eyebrow="What We Supply"
          title="Products and support for solar and electrical projects"
          lead="From individual components to complete project supply, sourced according to your requirement and availability."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <Link
                to={c.to}
                data-testid={`home-category-${i}`}
                className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/60 hover:shadow-[0_18px_40px_rgba(11,25,44,0.09)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-brand">
                  <c.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-navy">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{c.text}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-slate-50 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Products" title="Featured products" />
            <Reveal delay={0.1}>
              <Link to="/products" className="group inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-brand" data-testid="home-view-all-products-link">
                View all products
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <Link
                  to="/products"
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(11,25,44,0.09)]"
                  data-testid={`home-featured-product-${p.id}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-semibold text-navy">{p.name}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.description}</p>
                    <span className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Price on Request</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
        <SectionHeading eyebrow="Services" title="From enquiry to installation support" />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className="h-full">
              <Link
                to="/services"
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(11,25,44,0.09)]"
                data-testid={`home-service-${s.id}`}
              >
                <span className="font-display text-4xl font-bold text-slate-100 transition-colors duration-300 group-hover:text-brand/30">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.description}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Presence */}
      <section className="bg-navy py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand">Our Presence</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                  Delhi · Patna · Silvassa
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <Link
                to="/presence"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-white transition-colors hover:text-brand"
                data-testid="home-view-presence-link"
              >
                View all locations
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {OFFICES.map((o, i) => (
              <Reveal key={o.city} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7" data-testid={`home-office-${o.city.toLowerCase()}`}>
                  <MapPin className="h-5 w-5 text-brand" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{o.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{o.address}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-slate-50 px-7 py-12 text-center sm:px-12" data-testid="home-cta">
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              Tell us about your requirement
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Share your product list, project details, or technical specifications, and our team
              will respond with availability and pricing.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                data-testid="home-cta-contact-button"
                className="inline-flex items-center rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy-light"
              >
                Submit an Enquiry
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-cta-whatsapp-button"
                className="inline-flex items-center rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-brand hover:text-brand"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Home;